import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cn } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiCheckbox, NoddiDummyButton } from 'noddi-ui-web';
import { formatPriceListDiscountLabel, isLockedBookingSalesItem } from 'noddi-util';
import { CatalogPrice, getCatalogPayableText } from 'noddi-web';
import type { StationaryService } from '../../../types/stationaryBooking';
import { getCommonTranslations } from '../../../utils/commonTranslations';

type ServiceCardProps = {
  service: StationaryService;
  isAlreadySelected: boolean;
  handleServiceClick: (service: StationaryService) => void;
  contextPriceListName?: string | null;
};

/** Interactive primary/addon service tile for stationary catalog selection. */
export const ServiceCard = ({
  service,
  isAlreadySelected,
  handleServiceClick,
  contextPriceListName
}: ServiceCardProps) => {
  const isFree = service.grossPrice.amount === 0;
  const isLocked = isLockedBookingSalesItem(service);
  const name = service.name;
  const freeLabel = t`Free`;
  const priceFormatted = getCatalogPayableText({
    payable: service.grossPrice,
    original: service.originalGrossPrice,
    freeLabel,
    hideWhenZero: true
  });
  const accessibleLabel = priceFormatted ? t`${name}, ${priceFormatted}` : name;
  const discountText = formatPriceListDiscountLabel({
    priceListName: service.priceListName,
    discountPercent: service.discountPercent,
    contextPriceListName,
    texts: getCommonTranslations().priceListDiscountLabel
  });

  return (
    <NoddiBasicCard
      separateAction
      accessibleLabel={accessibleLabel}
      onClick={isLocked ? undefined : () => handleServiceClick(service)}
      selected={isAlreadySelected}
      className={cn(
        'flex flex-col gap-4 md:flex-row md:items-center md:justify-between',
        // Selection is an outline, not a fill: the brand color stays on the step's primary action.
        isAlreadySelected && 'border-2 border-card-selected-background bg-card text-card-foreground'
      )}
    >
      <div className='flex items-start gap-4'>
        <div className='mt-1' inert>
          {/* Indicator only - the card owns the toggle, so keep it out of the tab order. */}
          <NoddiCheckbox checked={isAlreadySelected} tabIndex={-1} aria-hidden />
        </div>
        <div className='flex flex-col gap-1'>
          <p className='text-lg font-medium'>{name}</p>
          <div className='flex flex-wrap items-baseline gap-2'>
            <CatalogPrice
              layout='inline'
              payable={service.grossPrice}
              original={service.originalGrossPrice}
              hideWhenZero
              freeLabel={freeLabel}
              payableClassName={cn('font-semibold', isFree && !isAlreadySelected && 'text-successDark')}
            />
            {discountText && (
              <span className='rounded-md bg-card-light-background px-2 py-0.5 text-xs font-medium text-primary'>
                {discountText}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className='flex justify-end'>
        <NoddiDummyButton
          variant='outline'
          size='small'
          startIcon={isLocked ? 'Lock' : undefined}
          className={cn(
            isAlreadySelected &&
              'border-card-selected-background bg-card-light-background text-primary md:hover:bg-card-light-background md:hover:text-primary'
          )}
        >
          {isLocked ? <Trans>Included</Trans> : isAlreadySelected ? <Trans>Added</Trans> : <Trans>Add</Trans>}
        </NoddiDummyButton>
      </div>
    </NoddiBasicCard>
  );
};
